import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { OrganizationService } from '~/app/services/organization/organization.service';
import { OrganizationRelationshipService } from '~/app/services/organization-relationship/organization-relationship.service';
import type { CreateOrganizationRelationshipRequestDto } from '@evimo/client/models/CreateOrganizationRelationshipRequestDto';
import type { UpdateOrganizationRelationshipRequestDto } from '@evimo/client/models/UpdateOrganizationRelationshipRequestDto';
import type { CreateOrganizationRelationshipResponseDto } from '@evimo/client/models/CreateOrganizationRelationshipResponseDto';

export type GroupedRelationship = {
  orgId: string;
  orgName: string;
  relationships: CreateOrganizationRelationshipResponseDto[];
};

function normalizeRelationships(
  input: unknown,
): CreateOrganizationRelationshipResponseDto[] {
  if (Array.isArray(input)) {
    return input as CreateOrganizationRelationshipResponseDto[];
  }

  if (input && typeof input === 'object') {
    const maybeData = (input as { data?: unknown }).data;
    if (Array.isArray(maybeData)) {
      return maybeData as CreateOrganizationRelationshipResponseDto[];
    }

    const maybeItems = (input as { items?: unknown }).items;
    if (Array.isArray(maybeItems)) {
      return maybeItems as CreateOrganizationRelationshipResponseDto[];
    }
  }

  return [];
}

export function groupRelationships(
  relationships: CreateOrganizationRelationshipResponseDto[],
  orgMap: Map<string, string>,
): GroupedRelationship[] {
  const grouped = new Map<
    string,
    CreateOrganizationRelationshipResponseDto[]
  >();
  for (const rel of relationships) {
    const group = grouped.get(rel.orgId) ?? [];
    group.push(rel);
    grouped.set(rel.orgId, group);
  }
  return Array.from(grouped.entries()).map(([orgId, rels]) => ({
    orgId,
    orgName: orgMap.get(orgId) ?? orgId,
    relationships: rels,
  }));
}

export function useOrganizationRelationships(onMutationSettled?: () => void) {
  const { t } = useTranslation('organizationRelationship');
  const queryClient = useQueryClient();

  const { data: organizations = [], isLoading: orgsLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => OrganizationService.findAllOrganizations(),
  });

  const { data: relationshipData, isLoading: relsLoading } = useQuery({
    queryKey: ['organization-relationships'],
    queryFn: () => OrganizationRelationshipService.findAll(),
  });

  const relationships = normalizeRelationships(relationshipData).filter((rel) =>
    Boolean(rel?.id && rel?.orgId && rel?.partnerOrgId),
  );

  const isLoading = orgsLoading || relsLoading;
  const orgMap = new Map(organizations.map((o) => [o.id, o.name]));
  const orgOptions = organizations.map((o) => ({ label: o.name, value: o.id }));
  const grouped = groupRelationships(relationships, orgMap);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['organization-relationships'] });

  const createMutation = useMutation({
    mutationFn: (dto: CreateOrganizationRelationshipRequestDto) =>
      OrganizationRelationshipService.create(dto),
    onSuccess: () => {
      message.success(t('messages.createSuccess', 'Relationship created!'));
      invalidate();
      onMutationSettled?.();
    },
    onError: () =>
      message.error(t('messages.createError', 'Failed to create.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      dto: UpdateOrganizationRelationshipRequestDto;
    }) => OrganizationRelationshipService.update(id, dto),
    onSuccess: () => {
      message.success(t('messages.updateSuccess', 'Relationship updated!'));
      onMutationSettled?.();
      invalidate();
    },
    onError: () =>
      message.error(t('messages.updateError', 'Failed to update.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => OrganizationRelationshipService.delete(id),
    onSuccess: () => {
      message.success(t('messages.deleteSuccess', 'Relationship deleted!'));
      onMutationSettled?.();
      invalidate();
    },
    onError: () =>
      message.error(t('messages.deleteError', 'Failed to delete.')),
  });

  return {
    organizations,
    relationships,
    isLoading,
    orgMap,
    orgOptions,
    grouped,
    createMutation,
    updateMutation,
    deleteMutation,
  };
}
